import { useEffect } from 'react'
import type { FC } from 'react'
import PictureAsPdf from '@mui/icons-material/PictureAsPdf'
import { Box, Typography, useTheme, Skeleton, CircularProgress } from '@mui/material'
import { useDispatch } from 'react-redux'
import {
  documentsProvidersPatientsFilesStub,
  documentsProvidersPatientsFiles,
  CacheRule,
} from '@fireflyhealth/api'

import { useMutation, useQuery } from '~/shared/hooks/query'
import { queueNotification } from '~/shared/notifications/actions'

interface ChatMessageDocumentProps {
  id: number
  user: { id: number }
}

const ChatMessageDocument: FC<ChatMessageDocumentProps> = ({ id, user }) => {
  const dispatch = useDispatch()
  const { palette } = useTheme()

  const pathParams = { id: String(id), patient: String(user.id), provider: 'me' }

  const { data: stubResponse } = useQuery(
    documentsProvidersPatientsFilesStub.retrieveQueryKey(pathParams),
    () => documentsProvidersPatientsFilesStub.retrieve(pathParams, { cache: CacheRule.ALWAYS }),
    { staleTime: Infinity },
  )
  const stub = stubResponse?.data

  const {
    mutate: download,
    isPending: downloadLoading,
    error: downloadError,
  } = useMutation(
    () =>
      documentsProvidersPatientsFiles.retrieve(pathParams, {
        cache: CacheRule.ALWAYS,
      }),
    {
      onSuccess: ({ data }) => {
        window.open(data.file, '_blank')
      },
    },
  )

  useEffect(() => {
    if (downloadError) {
      dispatch(
        queueNotification({
          message: 'You do not have permission to view this file',
          variant: 'error',
        }),
      )
    }
  }, [downloadError, dispatch])

  return (
    <Box
      sx={{
        my: 1,
        px: 2,
        py: 1,
        borderRadius: 16,
        border: `0.1rem solid ${palette.grey[200]}`,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      {downloadLoading ? (
        <CircularProgress size={24} color="primary" />
      ) : (
        <PictureAsPdf color="primary" />
      )}
      <Box sx={{ ml: 1 }}>
        {stub ? (
          <Box
            component="button"
            onClick={() => {
              // Only render button clickable when there are no errors
              if (!downloadLoading && !downloadError) {
                download()
              }
            }}
            sx={{ bgcolor: 'common.white', padding: 0, border: 0, cursor: 'pointer' }}
          >
            <Typography>{stub.title}</Typography>
          </Box>
        ) : (
          <Typography>
            <Skeleton animation="wave" variant="rectangular" height="1em" width="16em" />
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default ChatMessageDocument
